// SECURITY: User identity (sub) is extracted from the verified Cognito ID token only.
// NEVER read userId from URL params, query strings, request bodies, or any other client-controlled source.
// The same pattern applies to every Lambda created in Phase 2+ (event.requestContext.authorizer?.jwt?.claims?.sub).
// See PITFALLS.md Pitfall 4.

import { fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';

export interface UserIdentity {
  sub: string;
  displayName: string | undefined;  // preferred_username > name > given_name, or undefined
  email: string | undefined;         // email claim from ID token, or undefined
  username: string;                  // Cognito internal username — never rendered in UI
}

/**
 * Returns the authenticated user's Cognito sub from the verified ID token.
 * Throws if no session or no sub claim present.
 */
export async function getUserSub(): Promise<string> {
  const session = await fetchAuthSession();
  const sub = session.tokens?.idToken?.payload?.sub;
  if (typeof sub !== 'string' || sub.length === 0) {
    throw new Error('No sub claim in ID token — user is not authenticated.');
  }
  return sub;
}

/**
 * Returns the authenticated user's email from the verified ID token.
 */
export async function getUserEmail(): Promise<string | undefined> {
  const session = await fetchAuthSession();
  const email = session.tokens?.idToken?.payload?.email;
  return typeof email === 'string' ? email : undefined;
}

/**
 * Returns full user identity for display purposes.
 */
export async function getUserIdentity(): Promise<UserIdentity> {
  const [session, user] = await Promise.all([fetchAuthSession(), getCurrentUser()]);
  const payload = session.tokens?.idToken?.payload;
  const sub = payload?.sub;
  if (typeof sub !== 'string') throw new Error('No sub claim');

  const preferredUsername = typeof payload?.preferred_username === 'string' ? payload.preferred_username : undefined;
  const name = typeof payload?.name === 'string' ? payload.name : undefined;
  const givenName = typeof payload?.given_name === 'string' ? payload.given_name : undefined;
  const email = typeof payload?.email === 'string' ? payload.email : undefined;

  const displayName = preferredUsername ?? name ?? givenName;

  return {
    sub,
    displayName,
    email,
    username: user.username,
  };
}

/**
 * Sign out — clears Amplify token storage. UI re-renders to Authenticator login.
 */
export async function signOutUser(): Promise<void> {
  await signOut();
}
